import { CSSProperties, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { NavLink, useNavigate } from "react-router";
import { MessageCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { chatSocket } from "../../../services/chatSocket";
import { WS_RECONNECTED } from "../../../services/wsClient";
import {
  ChatDeletedEvent,
  chatMessageDeleted,
  ChatMessageEvent,
  ChatPinnedEvent,
  chatPinnedReceived,
  ChatPresenceEvent,
  chatPresenceReceived,
  ChatReadEvent,
  chatReadReceived,
  chatTypingChanged,
  clearChat,
  receiveChatMessage,
  resyncChat,
  selectChatUnread,
} from "../../../store/chat-slice";
import ChatMessageToast from "./chatMessageToast";

const TYPING_VISIBLE_MS = 4000;
const MESSAGE_TOAST_MS = 5000;
const UNREAD_TITLE_PREFIX = /^\(\d+\) /;

const ChatNavButton = () => {
  const dispatch = useAppDispatch();
  const unread = useAppSelector(selectChatUnread);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  // The socket listeners below are registered once, so they reach the current navigate through a ref.
  useEffect(() => {
    navigateRef.current = navigate;
  });

  useEffect(() => {
    const baseTitle = document.title.replace(UNREAD_TITLE_PREFIX, "");
    document.title = unread ? `(${unread}) ${baseTitle}` : baseTitle;
    return () => {
      document.title = baseTitle;
    };
  }, [unread]);

  useEffect(() => {
    const onMessage = async (_event: string, data: unknown) => {
      const incoming = await dispatch(receiveChatMessage(data as ChatMessageEvent));
      if (!incoming) return;

      toast.custom(
        ({ id, visible }) => (
          <ChatMessageToast
            peer={incoming.peer}
            preview={incoming.preview}
            isVisible={visible}
            onDismiss={() => toast.dismiss(id)}
            onOpen={() => {
              toast.dismiss(id);
              navigateRef.current("/chat", { state: { conversationId: incoming.conversationId } });
            }}
          />
        ),
        { id: `chat-${incoming.conversationId}`, duration: MESSAGE_TOAST_MS },
      );
    };
    const onRead = (_event: string, data: unknown) => {
      dispatch(chatReadReceived(data as ChatReadEvent));
    };
    const onDeleted = (_event: string, data: unknown) => {
      dispatch(chatMessageDeleted(data as ChatDeletedEvent));
    };
    let typingTimer: ReturnType<typeof setTimeout>;
    const onTyping = (_event: string, data: unknown) => {
      dispatch(chatTypingChanged((data as { from: string }).from));
      clearTimeout(typingTimer);
      typingTimer = setTimeout(() => dispatch(chatTypingChanged(null)), TYPING_VISIBLE_MS);
    };
    const onPinned = (_event: string, data: unknown) => {
      dispatch(chatPinnedReceived(data as ChatPinnedEvent));
    };
    const onPresence = (_event: string, data: unknown) => {
      dispatch(chatPresenceReceived(data as ChatPresenceEvent));
    };
    const onReconnect = () => {
      dispatch(resyncChat());
    };

    dispatch(resyncChat());
    chatSocket.on("chat:message", onMessage, false);
    chatSocket.on("chat:read", onRead, false);
    chatSocket.on("chat:deleted", onDeleted, false);
    chatSocket.on("chat:typing", onTyping, false);
    chatSocket.on("chat:presence", onPresence, false);
    chatSocket.on("chat:pinned", onPinned, false);
    chatSocket.on(WS_RECONNECTED, onReconnect, false);

    return () => {
      chatSocket.off("chat:message", onMessage);
      chatSocket.off("chat:read", onRead);
      chatSocket.off("chat:deleted", onDeleted);
      chatSocket.off("chat:typing", onTyping);
      chatSocket.off("chat:presence", onPresence);
      chatSocket.off("chat:pinned", onPinned);
      clearTimeout(typingTimer);
      chatSocket.off(WS_RECONNECTED, onReconnect);
      dispatch(clearChat());
    };
  }, [dispatch]);

  return (
    <NavLink
      to="/chat"
      title="Chat"
      aria-label={unread ? `Chat, ${unread} unread messages` : "Chat"}
      className="group relative flex overflow-hidden rounded-full p-[1.5px] shadow-lg shadow-(color:--brand-primary)/25 transition-transform hover:scale-105 active:scale-95"
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            style={{ "--dur": unread ? "2.5s" : "6s" } as CSSProperties}
            className="admin-orbit-spin absolute -inset-full bg-[conic-gradient(from_0deg,transparent_0deg,var(--brand-primary-hover)_80deg,transparent_160deg,transparent_200deg,var(--brand-primary)_280deg,transparent_360deg)] motion-reduce:animate-none"
          />
          <span
            className={`relative flex items-center gap-2 overflow-hidden rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
              isActive
                ? "bg-linear-to-br from-(--brand-primary-hover) to-(--brand-primary) text-black"
                : "bg-(--app-chrome) text-(--brand-primary-hover) group-hover:bg-(--app-raised)"
            }`}
          >
            <span
              aria-hidden="true"
              className="admin-sheen absolute inset-y-0 left-0 w-1/3 bg-white/30 motion-reduce:hidden"
            />
            <MessageCircle size={18} strokeWidth={2.25} className="relative" />
            <span className="relative hidden sm:inline">Chat</span>
            {unread > 0 && (
              <span className="relative flex h-5 min-w-5">
                <span className="absolute inset-0 animate-ping rounded-full bg-rose-500/70 motion-reduce:animate-none" />
                <span className="relative flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] leading-none font-bold text-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              </span>
            )}
          </span>
        </>
      )}
    </NavLink>
  );
};

export default ChatNavButton;
